import React, { useState, useMemo, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import { ResellerTaxExemptType } from '@/types/gql-op-types';
import { Link, useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useToast } from '../../components/Toast/ToastContext';
import {
  rescueChipStatsQuery,
  RescueChipStatsData,
  rescueDeviceStatsQuery,
  RescueDeviceStatsData,
  rescueGwaDashboardStatsQuery,
  RescueGwaDashboardStatsData,
  rescueDevicesProgramQuery,
  RescueDevicesProgramData,
  type DevicesWaitlist,
  RescuePetGql,
  removePetMutation,
  rescueCreatePetFosterTransferMutation,
  rescueScheduleHandoffMutation,
  rescueRecallPetFosterTransferMutation,
  unassignDeviceMutation,
} from '../../lib/rescueQueries';
import { rescueDocumentOverviewQuery, type RescueDocumentOverviewData } from '../../lib/documentQueries';
import {
  recallChipMutation,
  reassignChipToResellerMutation,
} from '@/appview/ResellerChipDetails/ResellerChipDetails.graphql';
import { assignNewOwnerToChipMutation } from '@/appview/RegisterChipV2/RegisterChip.graphql';
import SearchBar from '../../components/SearchBar/SearchBar';
import Button from '../../components/Button/Button';
import useAllDeviceItems from '../../hooks/useAllDeviceItems';
import useRescueRoster, { type RosterFilter, type RosterPet } from '../../hooks/useRescueRoster';
import PetsTable, { PAGE_SIZE_OPTIONS, type PetRow, type PetsSortKey } from './PetsTable';
import RemovePetModal from './RemovePetModal';
import AssignDeviceModal from './AssignDeviceModal';
import AssignDeviceToPetModal from './AssignDeviceToPetModal';
import AddChipToPetModal from './AddChipToPetModal';
import ViewPetModal from './ViewPetModal';
import PetFormModal from './PetFormModal';
import TransferToAdopterModal from '../../components/TransferToAdopterModal/TransferToAdopterModal';
import TransferToFosterModal, {
  type FosterTransferInput,
} from '../../components/TransferToFosterModal/TransferToFosterModal';
import useFosterHomes from '../../hooks/useFosterHomes';
import { useRescueContext } from '../../lib/RescueContext';
import { formatScheduledFor, type ScheduledFor } from '../../lib/scheduling';
import RecallDeviceModal from '../../components/RecallDeviceModal/RecallDeviceModal';
import { mapRescueDeviceItemToDevice } from '../../lib/deviceAdapters';
import type { FosterHome } from '../../lib/fosterAdapters';
import TransferChipModal, { DeviceAppliedChipFailedError, type DeviceTransferAction } from '../Chips/TransferChipModal';
import { INITIAL_ADOPTER, type AdopterFormData } from '../Chips/OwnerFormFields';
import type { Chip, SortDir } from '../../lib/types';
import { petDeviceLink, SEARCH_DEBOUNCE_MS } from '../../lib/utils';
import { documentActionItems } from './documentActionItems';
import { mapAdopterFormToContactInfoInput } from '../../lib/chipAdapters';
import { useTransferPetWithChipToAdopter } from '../../lib/useTransferPetWithChipToAdopter';
import {
  trackDashboardViewed,
  trackActionItemClicked,
  trackPetViewed,
  trackPetRemoved,
  trackDeviceTransferToFoster,
  trackDeviceTransferToAdopt,
  trackDeviceRecalled,
  trackChipOwnerAdded,
  trackHandoffScheduled,
} from '../../analytics';
import { devicesAccess, isOnWaitlistPath, type DevicesAccess } from '../../lib/devicesAccess';
import styles from './Dashboard.module.scss';

type Urgency = 'warning' | 'error' | 'info';

const URGENCY_COLORS: Record<Urgency, { bg: string; text: string }> = {
  error: { bg: '#fbefee', text: '#e0352a' },
  warning: { bg: '#fffcbd', text: '#b8860b' },
  info: { bg: '#e1f0ff', text: '#1360f4' },
};

const RESCUE_ACCENT = '#009a44';

function getT2aSubtext(pending: number, completed: number) {
  const total = completed + pending;
  if (total === 0) return { text: 'No transfers yet', color: '' };
  const rate = completed / total;
  const pct = Math.round(rate * 100);
  let color: string;
  if (rate >= 0.8) color = 'var(--success-text, #1B7D3A)';
  else if (rate >= 0.6) color = 'var(--warning-text, #B8860B)';
  else color = 'var(--error-text, #E0352A)';
  return {
    text: `T2A rate: ${pct}% (${completed} completed, ${pending} pending)`,
    color,
  };
}

interface ActionItem {
  title: string;
  description: string;
  link: string;
  urgency: Urgency;
}

/**
 * Tri-state content for the GWA Redemptions card on the Dashboard.
 * Loading shows a neutral placeholder so we don't flash the "Not enrolled" CTA
 * before the query resolves.
 */
function getGwaCardContent(loading: boolean, data: RescueGwaDashboardStatsData | undefined) {
  if (loading && !data) {
    return { value: '\u2014' as string | number, subtext: null as string | null };
  }
  const program = data?.rescueGwaProgram ?? null;
  if (!program) {
    return { value: '\u2014' as string | number, subtext: 'Set up your program \u203A' };
  }
  return {
    value: program.monthRedemptions,
    subtext: `last 30 days \u00B7 ${program.totalRedemptions} total`,
  };
}

/**
 * Content for the GPS Devices card. Loading shows a neutral placeholder, rescues without devices see
 * the waitlist or the old setup prompt, and active rescues see the device count + T2A subtext.
 */
function getDevicesCardContent(
  access: DevicesAccess,
  waitlist: DevicesWaitlist | null,
  activeDevices: number,
  t2a: { text: string; color: string | null },
) {
  const empty = '\u2014' as string | number;
  switch (access) {
    case 'loading':
      return { value: empty, subtext: null as string | null, subtextColor: null as string | null };
    case 'selfEnroll':
      return { value: empty, subtext: 'Set up your program \u203A', subtextColor: null };
    case 'notJoined':
      return { value: empty, subtext: 'Join the waitlist \u203A', subtextColor: RESCUE_ACCENT };
    case 'waitlisted':
      return {
        value: empty,
        subtext: `On the waitlist since ${DateTime.fromISO(waitlist?.joinedAt ?? '').toFormat('LLL d')}`,
        subtextColor: null,
      };
    case 'active':
      return { value: activeDevices, subtext: t2a.text, subtextColor: t2a.color };
  }
}

const JOIN_WAITLIST_ACTION_ITEM: ActionItem = {
  title: 'Join the Fi Devices waitlist',
  description: 'Rescues are chosen as device capacity opens',
  link: '/rescue/devices',
  urgency: 'info',
};

function buildActionItems(pending: number, completed: number, outstandingDocuments: ActionItem[]): ActionItem[] {
  const items: ActionItem[] = [...outstandingDocuments];
  const total = completed + pending;
  if (total > 0 && pending > 0) {
    const rate = completed / total;
    const pct = Math.round(rate * 100);
    const urgency: Urgency = rate < 0.6 ? 'error' : rate < 0.8 ? 'warning' : 'info';
    items.push({
      title: `T2A acceptance rate is ${pct}%`,
      description: `${pending} adopter${
        pending === 1 ? ' hasn\u2019t' : 's haven\u2019t'
      } completed setup \u2014 consider resending setup emails`,
      link: '/rescue/devices',
      urgency,
    });
  }
  return items;
}

function useModal<T = void>() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<T | null>(null);
  return {
    open,
    data,
    show(value?: T) {
      setData(value ?? null);
      setOpen(true);
    },
    close() {
      setOpen(false);
      setData(null);
    },
  };
}

/**
 * The pet a modal is about, preferring the page on screen and falling back to the row it was
 * opened from.
 *
 * `pets` is one server page now. A mutation made from inside a modal can move its own pet off
 * that page — Add Chip bumps `updatedAt`, and the roster's default order is newest first — and
 * every mutation here refetches. A lookup against the page alone then returns nothing, so a
 * modal that renders null without its pet vanishes under the user mid-task, and the add-owner
 * submit quietly skips the device-transfer step. The page still wins whenever it has the row,
 * so a refetched pet stays fresh behind the overlay; the fallback only answers once it is gone.
 */
function matchPetById(rows: RosterPet[], opened: RosterPet | null, petId: string | null | undefined): RosterPet | null {
  if (!petId) {
    return null;
  }
  return rows.find((p) => p.id === petId) ?? (opened?.id === petId ? opened : null);
}

/** The same rule, for the flows that hold a chip rather than a pet id. */
function matchPetByChip(rows: RosterPet[], opened: RosterPet | null, chipGraphqlId: string): RosterPet | null {
  return rows.find((p) => p.chip?.id === chipGraphqlId) ?? (opened?.chip?.id === chipGraphqlId ? opened : null);
}

export default function Dashboard() {
  const history = useHistory();
  const { showToast } = useToast();
  const { orgName, taxExemptType, inventoryLedgerReads } = useRescueContext();
  // `searchInput` drives the field; `searchQuery` — what the roster query reads — lands one
  // delay later. Filtering used to happen client-side over a drained list, where a keystroke
  // cost nothing; it is now a request the server answers with joins and a count, so typing a
  // breed would otherwise fire one per letter.
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearchQuery(value), SEARCH_DEBOUNCE_MS);
  }

  const [statusFilter, setStatusFilter] = useState<RosterFilter | null>(null);

  // Chip counts — use the dedicated stats resolver which runs efficient
  // parallel COUNT queries instead of the paginated chips query whose
  // getManyAndCount() can timeout for large rescues.
  const { data: chipStatsData } = useQuery<RescueChipStatsData>(rescueChipStatsQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const chipStock = chipStatsData?.currentUser?.chipReseller?.stats?.totalStock ?? 0;
  const chipRegistered = chipStatsData?.currentUser?.chipReseller?.stats?.totalRegistered ?? 0;

  // Device / T2A stats
  const { data: deviceStatsData } = useQuery<RescueDeviceStatsData>(rescueDeviceStatsQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const deviceStats = deviceStatsData?.rescueDeviceStats;
  const activeDevices = deviceStats?.totalActiveDevices ?? 0;
  const pendingCount = deviceStats?.pendingAdoptionsCount ?? 0;
  const completedCount = deviceStats?.completedAdoptionsCount ?? 0;
  const t2a = getT2aSubtext(pendingCount, completedCount);
  // Nudge each document that needs the rescue to act: rejected, expired, or about to expire.
  // `loading` has to be part of the condition, not just the data: while the query is in flight the
  // list is empty, which is indistinguishable from "nothing is asked for" — without it, a rescue
  // whose documents are in order sees the nudges flash in on every cold load.
  const { data: documentsData, loading: documentsLoading } =
    useQuery<RescueDocumentOverviewData>(rescueDocumentOverviewQuery);
  const outstandingDocuments: ActionItem[] = documentsLoading
    ? []
    : documentActionItems(
        documentsData?.rescueDocumentOverview?.documents ?? [],
        taxExemptType === ResellerTaxExemptType.GOVERNMENT_ENTITY,
      ).map((item) => ({ ...item, link: '/rescue/account' }));

  // GWA enrollment + redemption counts
  const { data: gwaStatsData, loading: gwaStatsLoading } = useQuery<RescueGwaDashboardStatsData>(
    rescueGwaDashboardStatsQuery,
    { fetchPolicy: 'cache-and-network' },
  );
  const gwaCard = getGwaCardContent(gwaStatsLoading, gwaStatsData);

  // Devices program enrollment — drives the GPS Devices card's CTA state.
  const { data: devicesEnrollmentData } = useQuery<RescueDevicesProgramData>(rescueDevicesProgramQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const devicesAccessState = devicesAccess(devicesEnrollmentData);
  const devicesCard = getDevicesCardContent(
    devicesAccessState,
    devicesEnrollmentData?.rescueDevicesProgram.waitlist ?? null,
    activeDevices,
    t2a,
  );
  const builtActionItems = buildActionItems(pendingCount, completedCount, outstandingDocuments);
  const actionItems =
    devicesAccessState === 'notJoined' ? [JOIN_WAITLIST_ACTION_ITEM, ...builtActionItems] : builtActionItems;

  // ── The pet roster ───────────────────────────
  //
  // One query: `rescueRoster` returns the page the table renders, already filtered by the
  // search and the status chip, ordered by the column the user picked, and carrying the
  // status, timestamp, adopter snapshot and chip each row shows.
  //
  // It replaces a merge this page used to do by hand — every pet in the household, cross-
  // referenced against three drained `rescueDevices` filters plus a drain of the reseller's
  // registered chips — purely to derive a status column. The chip fallback in particular is
  // now a join: `pet.chip` goes null for a pending or adopted pet once the rescue's household
  // membership lapses, which is the only reason that chip drain existed.
  const [rosterSort, setRosterSort] = useState<{ key: PetsSortKey; dir: SortDir } | null>(null);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const roster = useRescueRoster(searchQuery, statusFilter, pageSize, rosterSort);
  const pets = roster.pets;

  // The table opens on Status descending, which is also the server's default for this query —
  // so `rosterSort` is null until the user picks a column, and the header shows what the
  // server is already doing rather than claiming an ordering nobody asked for.
  const activeSortKey = rosterSort?.key ?? 'status';
  const activeSortDir = rosterSort?.dir ?? 'desc';

  function handleRosterSort(key: PetsSortKey) {
    setRosterSort((prev) => {
      const current = prev ?? { key: 'status' as PetsSortKey, dir: 'desc' as SortDir };
      if (current.key === key) {
        return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: 'asc' };
    });
  }

  /**
   * The IN_FOSTER placements, kept only to build the `Device` view-model the recall modal
   * needs — holder name, Safe Zone and WiFi state — which a roster row does not carry.
   *
   * The one drain left on this page, and it outlives B4 rather than waiting on it:
   * `rescueRoster` is merged, but it answers for the roster, not for the placement behind one
   * pet. `rescuePet(petId)` is the closest thing on main and stops short — it returns the pet,
   * transfer and scheduled hand-off, and none of the device state (`hasSafeZone`,
   * `hasWifiSetup`, `batteryPercent`, `isOnline`) that only `RescueDeviceItem` carries.
   * Replacing this drain wants a resolver returning both for a single pet.
   */
  const foster = useAllDeviceItems('IN_FOSTER');

  // `rescuePetsQuery` is no longer fetched here. It survives as the picker source for the
  // assign / register / edit modals — which want every pet in the household rather than a page
  // of a roster that also lists pets already placed or adopted out — and each of those opens
  // its own copy, so the Dashboard was holding a second, unread one.

  const mutationOpts = { refetchQueries: 'active' as const };
  const [removePetMut] = useMutation(removePetMutation, mutationOpts);
  const [createFosterTransferMut] = useMutation(rescueCreatePetFosterTransferMutation, mutationOpts);
  const [scheduleHandoffMut] = useMutation(rescueScheduleHandoffMutation, mutationOpts);
  const [recallFosterTransferMut] = useMutation(rescueRecallPetFosterTransferMutation, mutationOpts);
  const [unassignDeviceMut] = useMutation(unassignDeviceMutation, mutationOpts);
  const [recallChipMut] = useMutation(recallChipMutation, mutationOpts);
  const [reassignToReseller] = useMutation(reassignChipToResellerMutation, mutationOpts);
  const [assignNewOwner] = useMutation(assignNewOwnerToChipMutation, mutationOpts);
  const transferPetWithChip = useTransferPetWithChipToAdopter(mutationOpts);

  const removePetModal = useModal<{
    id: string;
    name: string;
    species?: string | null;
    breed?: { name: string } | null;
    chip?: { id: string } | null;
    device?: { id: string } | null;
  }>();
  const fosterModal = useModal<{ moduleId: string; petId: string; petName: string | null }>();
  const recallModal = useModal<{ moduleId: string; petId: string }>();
  // Backs the shared modals' roster picker. Unpaginated and placement-heavy, and every mutation here
  // refetches active queries — so only fetch it once a modal that needs it is actually open.
  const fosterHomes = useFosterHomes({ skip: !fosterModal.open && !recallModal.open });
  const assignDeviceModal = useModal<string>();
  const viewPetModal = useModal<string>();
  const createPetModal = useModal();
  const editPetModal = useModal<string>();
  const addChipModal = useModal<{
    id: string;
    name: string;
    breed?: { name: string } | null;
    /** Prefills the adopter's contact when the pet is mid-adoption; the roster row carries it. */
    pendingAdoption?: { fullName: string | null; email: string | null; phone: string | null } | null;
  }>();
  const recallTarget = useMemo(() => {
    const petId = recallModal.data?.petId;
    if (!petId) {
      return null;
    }
    const item = foster.items.find((entry) => entry.pet?.id === petId);
    return item ? mapRescueDeviceItemToDevice(item, orgName, 'IN_FOSTER') : null;
  }, [recallModal.data?.petId, foster.items, orgName]);

  // The row a modal was opened from — see `matchPetById` for why the page alone is not enough.
  const [openedPet, setOpenedPet] = useState<RosterPet | null>(null);
  const viewPetTarget = matchPetById(pets, openedPet, viewPetModal.data);
  const editPetTarget = matchPetById(pets, openedPet, editPetModal.data);
  const assignToPetModal = useModal<RescuePetGql>();
  const adopterModal = useModal<{
    moduleId: string;
    petId: string;
    petName: string | null;
    // The chip's GraphQL `id` field — what assignNewOwnerToChip expects as
    // `chipId`. (Backend resolver returns shortId, so this equals shortId.)
    chipId: string | null;
    chipSerialNumber: string | null;
  }>();
  const addOwnerModal = useModal<Chip>();

  // Memoize the device hand-off prop for TransferChipModal so the modal sees a
  // stable object reference until the underlying chip/pet pairing changes.
  const addOwnerDevice = useMemo(() => {
    const chip = addOwnerModal.data;
    if (!chip) return null;
    const pet = matchPetByChip(pets, openedPet, chip._graphqlId);
    return pet?.device ? { moduleId: pet.device.moduleId } : null;
  }, [addOwnerModal.data, pets, openedPet]);

  function handleOpenRemovePet(petId: string, petName: string) {
    // Reachable from `ViewPetModal`, which outlives the page its row came from — the same
    // reason the targets above take the fallback. Missing it renders "No chip · No device"
    // in the confirm modal for a pet that has both.
    const pet = matchPetById(pets, openedPet, petId);
    removePetModal.show({
      id: petId,
      name: petName,
      species: pet?.species ?? null,
      breed: pet?.breed ?? null,
      chip: pet?.chip ? { id: pet.chip.id } : null,
      device: pet?.device ? { id: pet.device.id } : null,
    });
  }

  async function handleConfirmRemovePet(petId: string, petName: string) {
    const pet = matchPetById(pets, openedPet, petId);
    const hadChip = !!pet?.chip;
    const hadDevice = !!pet?.device;
    try {
      await removePetMut({ variables: { subjectId: petId } });
      trackPetRemoved(hadChip, hadDevice);
      showToast(`${petName} removed`);
    } catch (err) {
      showToast(`Failed to remove pet: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleViewPet(pet: PetRow) {
    trackPetViewed(!!pet.chip, !!pet.device, pet.status ?? 'rescue', pet.species ?? undefined);
    // Resolved rather than stored as handed over: the table's `PetRow` has no slot for the
    // adopter snapshot that `AddChipToPetModal`'s overwrite guard reads off the roster row.
    setOpenedPet(matchPetById(pets, openedPet, pet.id));
    viewPetModal.show(pet.id);
  }

  function handleEditPet(petId: string) {
    setOpenedPet(matchPetById(pets, openedPet, petId));
    editPetModal.show(petId);
  }

  function handleOpenAddChip(petId: string) {
    const pet = matchPetById(pets, openedPet, petId);
    if (pet) {
      addChipModal.show(pet);
    }
  }

  function handleOpenAssignDevice(petId: string) {
    // Also offered inside `ViewPetModal`. Without the fallback the lookup misses and this
    // silently opens the device-first picker instead of the one the user asked for.
    const pet = matchPetById(pets, openedPet, petId);
    if (pet) {
      assignToPetModal.show(pet);
    } else {
      assignDeviceModal.show(petId);
    }
  }

  /**
   * Books a hand-off for later. Errors are rethrown so the modal can keep itself open and show them
   * on the schedule step.
   */
  async function scheduleHandoff(
    petId: string,
    kind: 'ADOPTION' | 'FOSTER',
    when: ScheduledFor,
    contact: { firstName: string; lastName?: string; email?: string; phone?: string; saveToRoster?: boolean },
    chipId?: string | null,
    entryPoint?: string,
  ) {
    await scheduleHandoffMut({
      variables: {
        input: {
          petId,
          kind,
          ...when,
          firstName: contact.firstName,
          lastName: contact.lastName || undefined,
          email: contact.email || undefined,
          phoneNumber: contact.phone || undefined,
          saveToRoster: contact.saveToRoster,
          chipId: kind === 'FOSTER' ? undefined : (chipId ?? undefined),
        },
      },
    });

    const hoursOut = Math.round((new Date(when.scheduledFor).getTime() - Date.now()) / 3_600_000);
    trackHandoffScheduled(entryPoint, { kind, hoursOut });
    showToast(`Scheduled for ${formatScheduledFor(when.scheduledFor, when.scheduledForTimezone)}`);
  }

  async function handleTransferToAdopter(firstName: string, lastName: string, email: string, phone: string) {
    // Capture modal data before the modal closes and clears its data.
    const petId = adopterModal.data?.petId;
    const moduleId = adopterModal.data?.moduleId ?? '';
    const chipId = adopterModal.data?.chipId ?? null;
    if (!petId) {
      return;
    }

    let result;
    try {
      result = await transferPetWithChip({
        petId,
        chipId,
        adopter: {
          ...INITIAL_ADOPTER,
          firstName,
          lastName,
          email,
          phone,
        },
      });
      trackDeviceTransferToAdopt('dashboard_pet_row');
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return;
    }

    if (result.chipAssigned) {
      trackChipOwnerAdded('dashboard_t2a');
      showToast(`Transfer initiated for ${moduleId}; chip ownership updated`);
    } else if (result.chipError) {
      const detail = result.chipError instanceof Error ? result.chipError.message : 'Unknown error';
      showToast(`Transfer initiated for ${moduleId}, but chip transfer failed: ${detail}`);
    } else {
      showToast(`Transfer initiated for ${moduleId}`);
    }
  }

  // Named `transfer`, not `foster`: the page already binds `foster` to the IN_FOSTER device list
  // that `recallTarget` reads from, and shadowing it here would quietly resolve to the wrong one.
  async function handleTransferToFoster(petId: string, transfer: FosterTransferInput) {
    const { firstName, lastName, email, phone, saveToRoster } = transfer;
    try {
      await createFosterTransferMut({
        variables: {
          input: {
            petId,
            email,
            firstName,
            lastName: lastName || undefined,
            phoneNumber: phone,
            // false leaves the home off the roster, so recall tears it down.
            saveToRoster,
          },
        },
      });
      trackDeviceTransferToFoster('dashboard_pet_row', { fosterSource: transfer.source, saveToRoster });
      showToast('Assigned to foster');
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleRecallDevice(moduleId: string, petId: string, saveToRoster: boolean) {
    try {
      // false leaves the home off the roster, so recall tears it down.
      await recallFosterTransferMut({ variables: { input: { petId, saveToRoster } } });
      trackDeviceRecalled('dashboard_pet_row', { saveToRoster });
      showToast(`${moduleId} recalled to rescue`);
    } catch (err) {
      showToast(`Failed to recall: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleUnassignDevice(moduleId: string, petId: string) {
    try {
      await unassignDeviceMut({ variables: { moduleId, petId } });
      showToast(`Pet removed from ${moduleId}`);
    } catch (err) {
      showToast(`Failed to remove: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleDeviceAction(actionKey: string, moduleId: string, petId: string) {
    if (actionKey === 'view') {
      history.push(petDeviceLink(moduleId, pets.find((p) => p.id === petId)?.status));
    } else if (actionKey === 'transferAdopter') {
      const pet = pets.find((p) => p.id === petId);
      adopterModal.show({
        moduleId,
        petId,
        petName: pet?.name ?? null,
        chipId: pet?.chip?.id ?? null,
        chipSerialNumber: pet?.chip?.serialNumber ?? null,
      });
    } else if (actionKey === 'transferFoster') {
      const fosterPet = pets.find((p) => p.id === petId);
      fosterModal.show({ moduleId, petId, petName: fosterPet?.name ?? null });
    } else if (actionKey === 'recallDevice') {
      recallModal.show({ moduleId, petId });
    } else if (actionKey === 'removeFromPet') {
      handleUnassignDevice(moduleId, petId);
    }
  }

  async function handleChipAction(
    actionKey: string,
    chipId: string,
    shortId: string,
    serialNumber: string,
    petId: string,
  ) {
    if (actionKey === 'viewChip') {
      history.push(`/rescue/chips?search=${encodeURIComponent(shortId)}`);
    } else if (actionKey === 'addOwner') {
      const pet = matchPetById(pets, openedPet, petId);
      if (!pet?.chip) return;
      // Held for the submit handler, which resolves the pet again once the modal has been open
      // across a refetch or two.
      setOpenedPet(pet);
      // Construct the minimal Chip view-model that TransferChipModal renders
      // (id, petName, species, breed) plus _graphqlId for the submit mutation.
      addOwnerModal.show({
        _graphqlId: pet.chip.id,
        id: pet.chip.shortId,
        fullChipId: pet.chip.serialNumber,
        petName: pet.name,
        species: pet.species ?? null,
        breed: pet.breed?.name ?? null,
      } as Chip);
    } else if (actionKey === 'clearOwner') {
      try {
        // Reassign to reseller to get the ResellerChip ID, then recall to clear owner
        const { data } = await reassignToReseller({
          variables: { input: { serialNumber, clearOwnerInfo: false } },
        });
        const resellerChipId = data?.reassignChipToReseller?.id;
        if (resellerChipId) {
          await recallChipMut({ variables: { input: { chipId: resellerChipId } } });
        }
        showToast('Owner cleared');
      } catch (err) {
        showToast(`Failed to clear owner: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }
  }

  async function handleAddOwnerSubmit(_chipId: string, adopter: AdopterFormData, deviceAction?: DeviceTransferAction) {
    // TransferChipModal passes its display id back; the mutation needs the
    // GraphQL chip id, which we stashed on the modal data when opening.
    const chip = addOwnerModal.data;
    if (!chip?._graphqlId) return;
    const pet = matchPetByChip(pets, openedPet, chip._graphqlId);

    if (deviceAction === 'transfer' && pet?.device) {
      const moduleId = pet.device.moduleId;
      let result;
      try {
        result = await transferPetWithChip({
          petId: pet.id,
          chipId: chip._graphqlId,
          adopter,
        });
      } catch (err) {
        const detail = err instanceof Error ? err.message : 'Unknown error';
        showToast(`Failed to add owner: ${detail}`);
        throw err;
      }
      trackDeviceTransferToAdopt('dashboard_add_owner');
      showToast(`Transfer initiated for ${moduleId}`);
      if (result.chipError) {
        throw DeviceAppliedChipFailedError.fromDeviceAction(
          `${moduleId} adoption transfer was initiated`,
          result.chipError instanceof Error ? result.chipError.message : 'Unknown error',
        );
      }
      trackChipOwnerAdded('dashboard_pet_row');
      return;
    }

    let deviceApplied: { description: string } | null = null;
    try {
      if (deviceAction === 'return' && pet?.device) {
        const moduleId = pet.device.moduleId;
        await unassignDeviceMut({ variables: { moduleId, petId: pet.id } });
        showToast(`${moduleId} returned to rescue stock`);
        deviceApplied = { description: `${moduleId} was returned to rescue stock` };
      }

      await assignNewOwner({
        variables: {
          input: {
            chipId: chip._graphqlId,
            contactInfo: mapAdopterFormToContactInfoInput(adopter),
          },
        },
      });
      trackChipOwnerAdded('dashboard_pet_row');
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Unknown error';
      if (deviceApplied) {
        throw DeviceAppliedChipFailedError.fromDeviceAction(deviceApplied.description, detail);
      }
      showToast(`Failed to add owner: ${detail}`);
      throw err;
    }
  }

  // ── Analytics ──────────────────────────

  const tracked = useRef(false);
  useEffect(() => {
    // Also wait on the documents query: it feeds action items, so firing before it settles would
    // report a count short by however many documents are outstanding.
    if (!tracked.current && (chipStatsData || deviceStatsData) && !documentsLoading) {
      tracked.current = true;
      const total = pendingCount + completedCount;
      const rate = total > 0 ? Math.round((completedCount / total) * 100) : 0;
      trackDashboardViewed(actionItems.length, rate);
    }
  }, [chipStatsData, deviceStatsData, documentsLoading]); // eslint-disable-line react-hooks/exhaustive-deps -- tracked.current guard ensures single fire

  // Waitlisted rescues can't act on devices yet, so the card moves after the two programs they can.
  const devicesCardLast = isOnWaitlistPath(devicesAccessState);
  const devicesSummaryCard = (
    <Link to="/rescue/devices" className={styles.summaryCard}>
      <div className={styles.cardLabel}>GPS Devices</div>
      <div className={styles.cardValue}>{devicesCard.value}</div>
      {devicesCard.subtext && (
        <div
          className={styles.cardSubtext}
          style={devicesCard.subtextColor ? { color: devicesCard.subtextColor, fontWeight: 600 } : undefined}
        >
          {devicesCard.subtext}
        </div>
      )}
    </Link>
  );

  return (
    <div>
      <h1>Dashboard</h1>

      <div className={styles.summaryCards}>
        <Link to="/rescue/chips" className={styles.summaryCard}>
          <div className={styles.cardLabel}>Fi Nano Microchips</div>
          <div className={styles.cardValue}>{chipStock + chipRegistered}</div>
          <div className={styles.cardSubtext}>
            {chipStock} in stock, {chipRegistered} registered
          </div>
        </Link>

        {!devicesCardLast && devicesSummaryCard}

        {inventoryLedgerReads && (
          <Link to="/rescue/devices" className={styles.summaryCard}>
            <div className={styles.cardLabel}>Lost &amp; Inoperable</div>
            <div className={styles.cardValue}>
              {(deviceStats?.lostCount ?? 0) + (deviceStats?.inoperableCount ?? 0)}
            </div>
            <div className={styles.cardSubtext}>
              {deviceStats?.lostCount ?? 0} lost, {deviceStats?.inoperableCount ?? 0} inoperable
            </div>
          </Link>
        )}

        <Link to="/rescue/gwa" className={styles.summaryCard}>
          <div className={styles.cardLabel}>GWA Redemptions</div>
          <div className={styles.cardValue}>{gwaCard.value}</div>
          {gwaCard.subtext && <div className={styles.cardSubtext}>{gwaCard.subtext}</div>}
        </Link>

        {devicesCardLast && devicesSummaryCard}
      </div>

      {actionItems.length > 0 && (
        <div className={styles.contentCard}>
          <div className={styles.contentCardHeader}>
            <h2 className={styles.contentCardTitle}>Action Items</h2>
          </div>
          <div className={styles.actionItems}>
            {actionItems.map((item) => {
              const colors = URGENCY_COLORS[item.urgency];
              return (
                <Link
                  key={item.title}
                  to={item.link}
                  className={styles.actionItem}
                  onClick={() => trackActionItemClicked(item.title, item.urgency)}
                >
                  <div className={styles.actionIcon} style={{ background: colors.bg, color: colors.text }}>
                    {item.urgency === 'error' ? '!' : item.urgency === 'warning' ? '!' : 'i'}
                  </div>
                  <div className={styles.actionText}>
                    <div className={styles.actionTitle}>{item.title}</div>
                    <div className={styles.actionMeta}>{item.description}</div>
                  </div>
                  <div className={styles.actionChevron}>&rsaquo;</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.quickActionsSection}>
        <div className={styles.quickActionsLabel}>Quick actions</div>
        <div className={styles.quickActions}>
          <Link to="/rescue/chips" className={styles.quickActionBtnBlue}>
            Register Chips
          </Link>
          <button type="button" className={styles.quickActionBtnGreen} onClick={() => assignDeviceModal.show('')}>
            Assign Device
          </button>
          <button type="button" className={styles.quickActionBtnGreen} onClick={() => createPetModal.show()}>
            Create a New Pet
          </button>
          <Link to="/rescue/devices" className={styles.quickActionBtnYellow}>
            View Devices
          </Link>
          <Link to="/rescue/help" className={styles.quickActionBtnYellow}>
            Get Help
          </Link>
        </div>
      </div>

      <div className={styles.searchRow}>
        <SearchBar value={searchInput} onChange={handleSearchChange} placeholder="Search by pet name or breed..." />
        <div className={styles.filterButtons}>
          <Button variant={statusFilter === null ? 'green' : 'ghost'} size="sm" onClick={() => setStatusFilter(null)}>
            All
          </Button>
          <Button
            variant={statusFilter === 'rescue' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('rescue')}
          >
            Rescue
          </Button>
          <Button
            variant={statusFilter === 'foster' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('foster')}
          >
            Foster
          </Button>
          <Button
            variant={statusFilter === 'adopted' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('adopted')}
          >
            Adopted
          </Button>
          <Button
            variant={statusFilter === 'no_device' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('no_device')}
          >
            No Device
          </Button>
        </div>
      </div>

      <PetsTable
        pets={pets}
        totalCount={roster.totalCount}
        currentPage={roster.currentPage}
        totalPages={roster.totalPages}
        pageSize={pageSize}
        onPageChange={roster.goToPage}
        onPageSizeChange={setPageSize}
        sortKey={activeSortKey}
        sortDir={activeSortDir}
        onSort={handleRosterSort}
        loading={roster.loading}
        transitioning={roster.transitioning}
        hasError={roster.error}
        onRetry={roster.refetch}
        onAssignDevice={handleOpenAssignDevice}
        onViewPet={handleViewPet}
        onCreatePet={() => createPetModal.show()}
        onDeviceAction={handleDeviceAction}
        onChipAction={handleChipAction}
        onAddChip={(pet) => handleOpenAddChip(pet.id)}
      />

      <RemovePetModal
        open={removePetModal.open}
        onClose={removePetModal.close}
        pet={removePetModal.data}
        onConfirm={handleConfirmRemovePet}
      />

      <AssignDeviceModal
        key={assignDeviceModal.data ?? ''}
        open={assignDeviceModal.open}
        onClose={assignDeviceModal.close}
        initialPetId={assignDeviceModal.data ?? ''}
      />

      <AssignDeviceToPetModal
        open={assignToPetModal.open}
        onClose={assignToPetModal.close}
        pet={assignToPetModal.data}
      />

      <AddChipToPetModal open={addChipModal.open} onClose={addChipModal.close} pet={addChipModal.data} />

      <ViewPetModal
        open={viewPetModal.open}
        onClose={viewPetModal.close}
        pet={viewPetTarget}
        onAssignDevice={handleOpenAssignDevice}
        onAddChip={handleOpenAddChip}
        onRemovePet={handleOpenRemovePet}
        onEditPet={handleEditPet}
      />

      <PetFormModal mode="create" open={createPetModal.open} onClose={createPetModal.close} />

      <PetFormModal mode="edit" open={editPetModal.open} onClose={editPetModal.close} pet={editPetTarget} />

      <TransferToAdopterModal
        open={adopterModal.open}
        onClose={adopterModal.close}
        deviceId={adopterModal.data?.moduleId ?? ''}
        petName={adopterModal.data?.petName ?? null}
        chipSerialNumber={adopterModal.data?.chipSerialNumber ?? null}
        showRescueDiscount
        onTransfer={handleTransferToAdopter}
        onSchedule={async (details, when) => {
          const data = adopterModal.data;
          if (data?.petId) {
            await scheduleHandoff(data.petId, 'ADOPTION', when, details, data.chipId, 'dashboard_t2a');
          }
        }}
      />

      <RecallDeviceModal
        open={recallModal.open}
        onClose={recallModal.close}
        device={recallTarget}
        homes={fosterHomes.homes}
        homesLoaded={fosterHomes.loaded}
        onRecall={(saveToRoster) => {
          const data = recallModal.data;
          if (data) {
            handleRecallDevice(data.moduleId, data.petId, saveToRoster);
          }
        }}
      />

      <TransferToFosterModal
        open={fosterModal.open}
        onClose={fosterModal.close}
        deviceId={fosterModal.data?.moduleId ?? null}
        petName={fosterModal.data?.petName ?? null}
        homes={fosterHomes.homes}
        homesLoaded={fosterHomes.loaded}
        onSchedule={async (transfer, when) => {
          const petId = fosterModal.data?.petId;
          if (petId) {
            await scheduleHandoff(petId, 'FOSTER', when, transfer, null, 'dashboard_pet_row');
          }
        }}
        onTransfer={(transfer) => {
          const petId = fosterModal.data?.petId;
          if (petId) {
            handleTransferToFoster(petId, transfer);
          }
        }}
      />

      <TransferChipModal
        open={addOwnerModal.open}
        onClose={addOwnerModal.close}
        chip={addOwnerModal.data}
        device={addOwnerDevice}
        onTransfer={handleAddOwnerSubmit}
      />
    </div>
  );
}
